import { z } from 'zod';
import { CaseBriefOutputSchema } from './case-brief.js';
import { QualificationSchema } from './legal-plan.js';
import { QualificationEnrichieSchema } from './theme-frame.js';
import { ArgumentDraftSchema, ArgumentsListSchema } from './moyen-draft.js';
import { PretentionDraftSchema } from './pretention-draft.js';
import { JpPackSchema } from './jp-entry.js';
import { StrategistDecisionSchema, StrategistBundleSchema } from './strategist.js';
import { FinalPlanSchema } from './final-plan.js';

/**
 * Schémas des colonnes JSONB de `pipeline_step_log` (1 ligne par job).
 *
 * Chaque colonne `phase_X_*` reçoit un `PhaseLogEntry<PhaseOutput>` qui
 * combine la sortie de la phase + métriques (latency, tokens, modèle).
 *
 * Note sur le stockage : Redis a un TTL de 24h, ce n'est PAS un store de
 * logs. Les blobs lourds (markdown ① et ⑦) sont donc stockés directement
 * dans la colonne JSONB — volume acceptable avec rétention 90j.
 *
 * Voir hub doc § 4 ter (`docs/architecture/plan-detaille-v2.md`).
 */

export const PhaseMetricsSchema = z.object({
  latency_ms: z.number().int().min(0),
  tokens_in: z.number().int().min(0).optional(),
  tokens_out: z.number().int().min(0).optional(),
  model: z.string(),                               // ex: "xai/grok-4-1-reasoning"
}).strict();
export type PhaseMetrics = z.infer<typeof PhaseMetricsSchema>;

/** Wrapper générique : payload de la phase + métriques + timestamps. */
function makePhaseLogEntrySchema<T extends z.ZodTypeAny>(payloadSchema: T) {
  return z.object({
    payload: payloadSchema,
    metrics: PhaseMetricsSchema,
    started_at: z.string(),                        // ISO 8601
    completed_at: z.string(),                      // ISO 8601
  }).strict();
}

// ─────────────────────────────────────────────────────────────────────
// Schémas par phase (colonnes JSONB de pipeline_step_log)
// ─────────────────────────────────────────────────────────────────────

export const Phase1CaseBriefLogSchema = makePhaseLogEntrySchema(CaseBriefOutputSchema);
export type Phase1CaseBriefLog = z.infer<typeof Phase1CaseBriefLogSchema>;

export const Phase2LegalPlannerLogSchema = makePhaseLogEntrySchema(QualificationSchema);
export type Phase2LegalPlannerLog = z.infer<typeof Phase2LegalPlannerLogSchema>;

export const Phase3LegalFrameLogSchema = makePhaseLogEntrySchema(QualificationEnrichieSchema);
export type Phase3LegalFrameLog = z.infer<typeof Phase3LegalFrameLogSchema>;

export const Phase4ArgumentRefinerLogSchema = makePhaseLogEntrySchema(ArgumentsListSchema);
export type Phase4ArgumentRefinerLog = z.infer<typeof Phase4ArgumentRefinerLogSchema>;

/**
 * Phase 5 : on logue un résumé par argument dans `pipeline_step_log` (signaux
 * agrégés + warnings). Le détail JP-par-JP vit dans la table annexe
 * `pipeline_jp_analysis_log` (1 ligne par JP analysée).
 */
export const Phase5JpResearchLogSchema = makePhaseLogEntrySchema(
  z.object({
    per_argument: z.array(
      z.object({
        argument_id: z.string(),
        jp_pack_size: z.number().int().min(0),
        jp_ecartees_size: z.number().int().min(0),
        jp_signals: JpPackSchema.shape.jp_signals,
        warnings: z.array(z.string()),
      }).strict(),
    ),
  }).strict(),
);
export type Phase5JpResearchLog = z.infer<typeof Phase5JpResearchLogSchema>;

/**
 * Phase 6 : résumé des décisions par argument dans `pipeline_step_log`.
 * Le détail (bundle structuré) vit dans la table annexe `pipeline_strategist_log`.
 */
export const Phase6StrategistLogSchema = makePhaseLogEntrySchema(
  z.object({
    decisions_summary: z.array(
      z.object({
        arg_id: z.string(),
        decision: z.enum(['retenu', 'ecarte']),
      }).strict(),
    ),
    retained_count: z.number().int().min(0),
    dropped_count: z.number().int().min(0),
    bundle_meta: StrategistBundleSchema.shape.meta,
  }).strict(),
);
export type Phase6StrategistLog = z.infer<typeof Phase6StrategistLogSchema>;

export const Phase7FinalPlanLogSchema = makePhaseLogEntrySchema(FinalPlanSchema);
export type Phase7FinalPlanLog = z.infer<typeof Phase7FinalPlanLogSchema>;

// ─────────────────────────────────────────────────────────────────────
// Schémas V1-bis (pipeline live default depuis 2026-05-03)
//
// Pourquoi schémas séparés des V2 plus haut : la pipeline V1-bis (5 phases :
// LegalPlanner → ArgumentRefiner → JpResearch → ArgumentAggregator → PlanWriter)
// n'a PAS le concept de phase 1 (case_brief) ni de phase 3 (legal_frame). Les
// formes de payload diffèrent aussi (top-level fields, métriques per-argument).
// On préserve les schémas V2 ci-dessus pour ne pas casser `PlanDetailleV2Orchestrator`
// (V2 reste accessible derrière un feature flag) ; les rows historiques V2 en
// DB restent lisibles. V1-bis utilise les schémas dédiés ci-dessous.
// ─────────────────────────────────────────────────────────────────────

/**
 * V1-bis case_analysis (LegalPlanner output) — distinct du `CaseAnalysis`
 * V2 (qui modélise legal_matters/legal_specialization/client_position). Ici on
 * garde le format V1 historique du `GrokLegalPlanner.LegalPlannerOutputSchema`.
 */
export const V1bisCaseAnalysisSchema = z.object({
  summary: z.string(),
  legalContext: z.string(),
  keyIssues: z.array(z.string()),
}).strict();
export type V1bisCaseAnalysis = z.infer<typeof V1bisCaseAnalysisSchema>;

const V1bisModelMetricsSchema = z.object({
  latency_ms: z.number().int().min(0),
  model: z.string(),
  tokens_in: z.number().int().min(0).optional(),
  tokens_out: z.number().int().min(0).optional(),
}).strict();

/**
 * Phase 2 V1-bis — LegalPlanner :
 * caseAnalysis (3 champs descriptifs V1) + arguments[] (MoyenDraft, ex-ArgumentDraft)
 * + pretentions[] (chantier prétentions 2026-07-06 — OPTIONNEL en lecture : les rows
 * antérieures au chantier n'ont pas la clé).
 */
export const Phase2LegalPlannerV1bisLogSchema = z.object({
  caseAnalysis: V1bisCaseAnalysisSchema,
  arguments: z.array(ArgumentDraftSchema),
  pretentions: z.array(PretentionDraftSchema).optional(),
  metrics: V1bisModelMetricsSchema,
}).strict();
export type Phase2LegalPlannerV1bisLog = z.infer<typeof Phase2LegalPlannerV1bisLogSchema>;

/**
 * Phase 4 V1-bis — ArgumentRefiner (1 LLM call par argument).
 * `per_argument` capture latence + tokens par argument (audit cost).
 *
 * Champs de télémétrie de préparation globale (2026-07-22) : le contrôleur les
 * écrivait déjà mais le `.strict()` de `metrics` les rejetait — CHAQUE écriture
 * phase 4 échouait en `pipeline_log_phase4_failed` silencieux (le contrôleur
 * est en @ts-nocheck, TS ne voyait pas l'excès). Optionnels : read-compat avec
 * les rows antérieures. `warningCodes` porte les signaux de dégradation
 * (ARTICLE_VERBATIM_UNVERIFIABLE, ARGUMENT_REFINER_FALLBACK_INVALID_IDS,
 * BATCH_REFINER_TREE_REVERTED) consommés par le dashboard admin.
 */
export const Phase4ArgumentRefinerV1bisLogSchema = z.object({
  refined_arguments: z.array(ArgumentDraftSchema),
  metrics: z.object({
    latency_ms: z.number().int().min(0),
    model: z.string(),
    total_tokens_in: z.number().int().min(0),
    total_tokens_out: z.number().int().min(0),
    per_argument: z.array(z.object({
      argument_id: z.string(),
      latency_ms: z.number().int().min(0),
      tokens_in: z.number().int().min(0),
      tokens_out: z.number().int().min(0),
    }).strict()),
    warningCodes: z.array(z.string()).optional(),
    argumentsReordered: z.boolean().optional(),
    preparationMode: z.string().optional(),
    globalPreparationTimingsMs: z.object({
      globalFondementSearch: z.number().int().min(0),
      verbatimFetch: z.number().int().min(0),
      batchRefiner: z.number().int().min(0),
      // Pré-résolution Légifrance des suggestions du planner (défaut depuis
      // 2026-08-06, kill-switch FONDEMENTS_PREFETCH_DISABLED). Optionnel :
      // absent sur les rows antérieures ET quand le kill-switch est posé. À ajouter ICI AVANT tout forward
      // côté contrôleur — le .strict() rejette sinon chaque écriture phase 4
      // (répétition de l'incident 2026-07-22 documenté ci-dessus).
      prefetch: z.number().int().min(0).optional(),
    }).strict().optional(),
    verifiedFondementsCount: z.number().int().min(0).optional(),
    totalArticlesVerified: z.number().int().min(0).optional(),
  }).strict(),
}).strict();
export type Phase4ArgumentRefinerV1bisLog = z.infer<typeof Phase4ArgumentRefinerV1bisLogSchema>;

/**
 * Phase 5 V1-bis — JpResearch (per-argument funnel).
 * Counts traversent l'entonnoir : keywords → candidates → step1 → step2.
 */
export const Phase5JpResearchV1bisLogSchema = z.object({
  per_argument: z.array(z.object({
    argument_id: z.string(),
    keywords_generated: z.array(z.string()),
    /**
     * Codes chambre Cassation ciblés par le KeywordGenerator pour cet argument
     * (`cr` / `comm` / `soc` / `civ1` / `civ2` / `civ3` / `pl` / `mi`).
     * Persisté pour audit qualité — permet de vérifier la pertinence du
     * routing chambre par argument et de corréler avec les verdicts JpAnalyzer.
     * `nullable().optional()` pour les anciens jobs sans cette donnée.
     */
    chambres_generated: z.array(z.string()).nullable().optional(),
    candidates_count: z.number().int().min(0),
    step1_passed: z.number().int().min(0),
    step2_passed_garder: z.number().int().min(0),
    step2_passed_remodeler: z.number().int().min(0),
    step2_invalider: z.number().int().min(0),
    step2_non_pertinent: z.number().int().min(0),
    final_jp_pack: z.number().int().min(0),
    final_jp_ecartees: z.number().int().min(0),
    // Entonnoir corpus interne. `nullable().optional()` pour rétro-compat jobs déjà persistés.
    knn_decisions_count: z.number().int().min(0).nullable().optional(),
    // LEGACY read-compat (write-strict/read-relâché) : favourJudge retiré du funnel argument
    // (2026-07-02, remplacé par le rerank) → PLUS ÉCRIT sur les nouveaux jobs. Champs conservés ici
    // UNIQUEMENT pour que les rows déjà persistées continuent de parser. Ne pas réécrire.
    favour_judge_items_count: z.number().int().min(0).nullable().optional(),
    favour_judge_pour_count: z.number().int().min(0).nullable().optional(),
    step2_input_count: z.number().int().min(0).nullable().optional(),
    // Observabilité MassSearch par (keyword × juridiction).
    // Forme : { byJurisdictionAndKeyword: [...], totals: {...} } (cf. MassSearchStats).
    // `nullable().optional()` pour les anciens jobs sans cette donnée.
    mass_search_status: z.unknown().nullable().optional(),
    // Quotas effectifs après BalancedSelector.selectStrict.
    // Record<jurisdiction, count> ; `nullable().optional()` pour les anciens jobs.
    selected_by_jurisdiction: z.record(z.string(), z.number()).nullable().optional(),
    // Juridictions sous-quota (selectStrict accepte un total < demandé).
    under_quota_jurisdictions: z.array(z.string()).nullable().optional(),
  }).strict()),
  metrics: z.object({
    latency_ms: z.number().int().min(0),
    total_step1_calls: z.number().int().min(0),
    total_step2_calls: z.number().int().min(0),
    total_tokens_in: z.number().int().min(0),
    total_tokens_out: z.number().int().min(0),
  }).strict(),
}).strict();
export type Phase5JpResearchV1bisLog = z.infer<typeof Phase5JpResearchV1bisLogSchema>;

/**
 * Phase 6 V1-bis — ArgumentAggregator (consolidated_arguments persistés ici en
 * fat row). Le détail per-argument vit aussi dans `pipeline_strategist_log`
 * (reuse de l'infra annexe — 1 row = 1 argument consolidé).
 *
 * `consolidated_arguments` est typé `unknown` car le schéma `ConsolidatedArgument`
 * vit côté backend (`apps/backend/services/enhanced-jurisprudence/prompts/argumentAggregator.ts`)
 * et n'a pas vocation à être partagé front/back via `@hector/shared-types`.
 * La validation forte se fait côté backend avant l'INSERT.
 */
export const Phase6AggregatorV1bisLogSchema = z.object({
  consolidated_arguments: z.array(z.unknown()),
  metrics: z.object({
    latency_ms: z.number().int().min(0),
    total_tokens_in: z.number().int().min(0),
    total_tokens_out: z.number().int().min(0),
    per_argument: z.array(z.object({
      argument_id: z.string(),
      latency_ms: z.number().int().min(0),
      tokens_in: z.number().int().min(0),
      tokens_out: z.number().int().min(0),
    }).strict()),
  }).strict(),
}).strict();
export type Phase6AggregatorV1bisLog = z.infer<typeof Phase6AggregatorV1bisLogSchema>;

/**
 * Phase 7 V1-bis — PlanWriter Sonnet (markdown final streamé).
 * argument_count = match count de `## ARGUMENT N` dans le markdown.
 */
export const Phase7FinalPlanV1bisLogSchema = z.object({
  markdown: z.string(),
  argument_count: z.number().int().min(0),
  metrics: V1bisModelMetricsSchema,
}).strict();
export type Phase7FinalPlanV1bisLog = z.infer<typeof Phase7FinalPlanV1bisLogSchema>;

// ─────────────────────────────────────────────────────────────────────
// Ligne complète `pipeline_step_log` (utilisée pour serializer/parser)
// ─────────────────────────────────────────────────────────────────────

export const PipelineStatusSchema = z.enum(['running', 'completed', 'failed']);
export type PipelineStatus = z.infer<typeof PipelineStatusSchema>;

// 'v1-bis' : pipeline live default (LegalPlanner → ArgumentRefiner → JpResearch
// → ArgumentAggregator → PlanWriter Sonnet). 'v1' = ancien V1 historique
// (avant le refactor 2026-05-03). 'v2' = pipeline 7 phases (opt-in, alpha).
export const PipelineVersionSchema = z.enum(['v1', 'v1-bis', 'v2']);
export type PipelineVersion = z.infer<typeof PipelineVersionSchema>;

export const PipelineInputSummarySchema = z.object({
  template_type: z.string(),
  procedure_type: z.string().optional(),
  specialisation: z.string().optional(),
  additional_instructions: z.string().optional(),
}).strict();
export type PipelineInputSummary = z.infer<typeof PipelineInputSummarySchema>;

export const PipelineStepLogRowSchema = z.object({
  job_id: z.string().uuid(),
  case_id: z.string().uuid(),
  cabinet_id: z.string().uuid(),
  user_id: z.string().uuid(),
  pipeline_version: PipelineVersionSchema,

  status: PipelineStatusSchema,
  started_at: z.string(),
  completed_at: z.string().nullable(),
  total_latency_ms: z.number().int().min(0).nullable(),

  error_phase: z.number().int().min(1).max(7).nullable(),
  error_code: z.string().nullable(),
  error_message: z.string().nullable(),
  error_details: z.unknown().nullable(),

  input_summary: PipelineInputSummarySchema.nullable(),

  phase_1_case_brief: Phase1CaseBriefLogSchema.nullable(),
  phase_2_legal_planner: Phase2LegalPlannerLogSchema.nullable(),
  phase_3_legal_frame: Phase3LegalFrameLogSchema.nullable(),
  phase_4_argument_refiner: Phase4ArgumentRefinerLogSchema.nullable(),
  phase_5_jp_research: Phase5JpResearchLogSchema.nullable(),
  phase_6_strategist: Phase6StrategistLogSchema.nullable(),
  phase_7_final_plan_writer: Phase7FinalPlanLogSchema.nullable(),

  created_at: z.string(),
  updated_at: z.string(),
}).strict();
export type PipelineStepLogRow = z.infer<typeof PipelineStepLogRowSchema>;

// ─────────────────────────────────────────────────────────────────────
// Ligne `pipeline_jp_analysis_log` (1 par JP analysée)
// ─────────────────────────────────────────────────────────────────────

/**
 * Row schema `pipeline_jp_analysis_log` — 1 ligne par décision JP analysée.
 *
 * Discriminé orthogonalement par 2 axes :
 *  - `context_kind` ∈ {argument, question} : QUEL prompt a tourné dans le JpAnalyzer
 *  - `agent_caller` : QUELLE feature appelante a invoqué l'analyse
 *
 * En mode `argument`, tous les champs argumentatifs (verdict 4-val + signaux
 * `portee`/`apport_global` + narratifs `resume_jp`/`apport_pour_argument`/
 * `avis_synthese`/`principes_juridiques_extraits` + `motif_classification` +
 * `is_favorable_dispositif`) doivent être présents en cas de variant actif.
 * En mode `question`, le JpAnalyzer ne produit qu'un filtre binaire
 * (`verdict` ∈ {pertinent, non_pertinent} + `confidence`) et AUCUN champ
 * argumentatif — ils doivent rester `null` au niveau DB.
 *
 * D'où le schéma : tous ces champs sont `.nullable()` au niveau base, et un
 * `superRefine` exprime les contraintes conditionnelles par mode. Le DB
 * (migration 092) relaxe `argument_relation` + `motif_classification` à NULL
 * pour permettre l'INSERT en mode question.
 */
/**
 * Base shape (sans `superRefine`) — exposé en interne pour permettre aux
 * consumers de faire `.omit({ id: true, created_at: true })` (impossible
 * directement sur un `ZodEffects`, qui est ce que retourne `.superRefine(...)`).
 *
 * NE PAS exporter cet objet sans réfléchir : les contraintes conditionnelles
 * argument/question ne sont PAS appliquées dessus. Préférer
 * `PipelineJpAnalysisLogRowSchema` ou `PipelineJpAnalysisLogInsertSchema`.
 */
const _PipelineJpAnalysisLogRowBaseSchema = z.object({
  id: z.string().uuid(),
  job_id: z.string().uuid(),
  // En mode question il n'y a pas d'argument actif — on accepte un sentinel
  // string libre (ex: 'no-argument') OU une string non vide selon le caller.
  // La validation reste "string non vide" ; le caller question-mode pose un
  // marker explicite type 'question:<questionId>' pour audit downstream.
  argument_id: z.string(),

  // Identité JP — judilibre_id est l'ID interne Judilibre (privilégié pour
  // l'audit trail et le linking back vers l'API Judilibre).
  judilibre_id: z.string().nullable(),
  ecli: z.string().nullable(),
  pourvoi_number: z.string().nullable(),
  juridiction: z.string(),
  formation: z.string().nullable(),
  date_iso: z.string().nullable(),
  title: z.string().nullable(),

  // Classification D46 (4 valeurs — vocab benchmark 2026-05-01).
  // `.nullable()` pour permettre les rows mode question (aucun verdict
  // argumentatif). Migration 092 relaxe le NOT NULL DB correspondant.
  // Contrainte conditionnelle (argument ⇒ required) appliquée par superRefine.
  argument_relation: z.enum([
    'garder',
    'remodeler',
    'invalider',
    'non_pertinent',
  ]).nullable(),
  // `.nullable()` pour mode question (pas de motif argumentatif). Idem ci-dessus.
  motif_classification: z.string().nullable(),

  // Signaux compacts (NULL si non_pertinent OU mode question)
  portee: z.enum(['generale', 'ancree_cas_espece']).nullable(),
  apport_global: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]).nullable(),

  // Narratifs (NULL si non_pertinent — levier 2 d'allègement volume — OU mode question)
  resume_jp: z.string().nullable(),
  apport_pour_argument: z.string().nullable(),
  avis_synthese: z.string().nullable(),

  // Principes juridiques extraits (V1-bis — attribués à l'arrêt source).
  // NULL si non_pertinent OU mode question ; rempli sinon. Ajouté par migration 068.
  principes_juridiques_extraits: z.string().nullable(),

  // Méta
  // `cited_articles` (renommé depuis `visa` par migration 071) : tous les
  // articles mobilisés par la juridiction (visa formel + corps), fusion D33.
  cited_articles: z.array(z.string()).nullable(),
  solution_resume: z.string().nullable(),
  is_favorable_dispositif: z.boolean().nullable(),

  // Trace LLM
  model: z.string(),
  tokens_in: z.number().int().min(0).nullable(),
  tokens_out: z.number().int().min(0).nullable(),
  latency_ms: z.number().int().min(0).nullable(),
  created_at: z.string(),

  // Phase A — colonnes ajoutées par migration 070 pour le bench
  // `bench-evaluation-isolee-de-chaque-agent-sur-jobs-reels`. Permettent de
  // rejouer juge-jp-step1-lecture-neutre-isole (full_text), juge-jp-step2-
  // classification-isole (step1_raw) et juge-keyword-generator-isole
  // (keywords_used) depuis la DB seule.
  full_text: z.string().nullable(),
  step1_raw: z.record(z.unknown()).nullable(),
  keywords_used: z.array(z.string()).nullable(),

  // ── Task 15bis (jp-analysis convergence) — colonnes ajoutées par migration 091 ──
  // Les deux axes orthogonaux décrits dans `docs/features/jp-analysis/` :
  //  - `context_kind` = quel prompt a été utilisé (mode du JpAnalyzer)
  //  - `agent_caller` = quelle feature/entry point a invoqué l'analyse
  // `verdict` = verdict final post-downgrade (union des 8 valeurs des deux modes ;
  //            CHECK SQL côté DB reste lax — la validation forte vit ici).
  // `confidence` = uniquement en mode question (top-sorting des JP retenues).
  //
  // Source canonique TS : `apps/backend/services/jp-analysis/types/output.ts`.
  // Voir spec §7 (`docs/features/jp-analysis/specs/2026-05-08-convergence-design.md`).
  // `.optional()` (et non `.default(...)`) parce que les consumers backend
  // construisent ces rows via `Omit<PipelineJpAnalysisLogRow, 'id' | 'created_at' | 'job_id'>`
  // — un `.default(...)` rendrait le champ obligatoire dans le type inféré côté output
  // et casserait tous les callers existants tant que Task 17 (PipelineLogRepository
  // + plug-in dans JpResearchWorkflow / WorkflowGenerationJurisprudenceSearch) n'est
  // pas exécutée. Le seed de valeurs par défaut se fera au niveau du repository.
  context_kind: z.enum(['argument', 'question']).optional(),
  agent_caller: z
    .enum(['pipeline-v1bis-batch', 'case-discovery-v3', 'edition-v3', 'subagent-research'])
    .optional(),
  verdict: z.string().nullable().optional(),
  confidence: z.enum(['high', 'medium', 'low']).nullable().optional(),

  // ── Attribution dossier (issue #227, migration 115) ──
  // `case_id` = dossier auquel se rattache l'analyse JP, propagé depuis
  // `CallerContext.case_id` par `projectToLogRow`. Permet de requêter « toutes
  // les analyses JP du dossier X » directement sur cette table — auparavant
  // impossible (le `callerCtx.case_id` était calculé puis jeté, aucune colonne
  // de destination). NULL pour les recherches sans dossier actif (ex. subagent
  // `legal_research`). `.nullable().optional()` (même raison que les colonnes
  // Task 17 ci-dessus : seed côté repository, type non-breaking pour les
  // callers historiques qui ne renseignent pas encore le champ).
  case_id: z.string().uuid().nullable().optional(),
}).strict();

/**
 * Refinement appliqué sur la base shape — extrait dans une fonction nommée
 * pour pouvoir le ré-appliquer sur `.omit(...)` (les consumers qui parsent
 * un row avant INSERT n'ont pas `id` / `created_at`).
 */
function applyJpAnalysisLogRowRefinement(
  row: {
    context_kind?: 'argument' | 'question';
    argument_relation: 'garder' | 'remodeler' | 'invalider' | 'non_pertinent' | null;
    motif_classification: string | null;
    portee: 'generale' | 'ancree_cas_espece' | null;
    apport_global: 0 | 1 | 2 | 3 | null;
    resume_jp: string | null;
    apport_pour_argument: string | null;
    avis_synthese: string | null;
    principes_juridiques_extraits: string | null;
    is_favorable_dispositif: boolean | null;
    verdict?: string | null;
    confidence?: 'high' | 'medium' | 'low' | null;
  },
  ctx: z.RefinementCtx,
): void {
  // Contraintes conditionnelles mode argument / mode question.
  //
  // Pourquoi un superRefine plutôt qu'un z.discriminatedUnion :
  //  - les rows historiques (pré-Task 17, pré-091) n'ont pas `context_kind`
  //    setté — le schéma doit accepter cet état (legacy compat).
  //  - le repository injecte un default `context_kind = 'argument'` pour les
  //    callers V1-bis avant Zod.parse — on raisonne sur la valeur effective.
  //  - on garde une seule shape TS (PipelineJpAnalysisLogRow) consommable
  //    indifféremment par les deux modes ; la discrimination se fait runtime.
  //
  // Règles :
  //  - mode 'argument'  → tous les champs argumentatifs présents (non-null) :
  //                       argument_relation, motif_classification.
  //                       Les autres signaux/narratifs RESTENT nullable
  //                       (ils sont NULL pour les rows `non_pertinent`).
  //  - mode 'question'  → tous les champs argumentatifs DOIVENT être null
  //                       (pas de variant actif possible) ;
  //                       `verdict` ∈ {pertinent, non_pertinent} ;
  //                       `confidence` ∈ {high, medium, low}.
  const mode = row.context_kind ?? 'argument';

  if (mode === 'argument') {
    if (row.argument_relation === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['argument_relation'],
        message: 'argument_relation is required when context_kind=argument',
      });
    }
    if (row.motif_classification === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['motif_classification'],
        message: 'motif_classification is required when context_kind=argument',
      });
    }
    return;
  }

  // mode === 'question'
  const forbiddenWhenQuestion: Array<keyof typeof row> = [
    'argument_relation',
    'motif_classification',
    'portee',
    'apport_global',
    'resume_jp',
    'apport_pour_argument',
    'avis_synthese',
    'principes_juridiques_extraits',
    'is_favorable_dispositif',
  ];
  for (const k of forbiddenWhenQuestion) {
    if (row[k] !== null && row[k] !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [k as string],
        message: `${String(k)} must be null when context_kind=question (mode question is a binary relevance filter, no argumentative verdict)`,
      });
    }
  }
  if (row.verdict !== 'pertinent' && row.verdict !== 'non_pertinent') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['verdict'],
      message: `verdict must be 'pertinent' | 'non_pertinent' when context_kind=question (got ${JSON.stringify(row.verdict)})`,
    });
  }
  if (row.confidence === null || row.confidence === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['confidence'],
      message: 'confidence is required when context_kind=question',
    });
  }
}

/**
 * Schéma public — base shape + contraintes conditionnelles argument/question.
 * À utiliser pour parser des rows LUS depuis DB (avec `id` + `created_at`).
 */
export const PipelineJpAnalysisLogRowSchema =
  _PipelineJpAnalysisLogRowBaseSchema.superRefine(applyJpAnalysisLogRowRefinement);
export type PipelineJpAnalysisLogRow = z.infer<typeof _PipelineJpAnalysisLogRowBaseSchema>;

/**
 * Schéma pour INSERT — sans `id` ni `created_at` (générés par la DB).
 * Mêmes contraintes conditionnelles argument/question que le schéma public.
 *
 * Pourquoi un schéma dédié plutôt qu'un `.omit()` côté caller :
 * `PipelineJpAnalysisLogRowSchema` est un `ZodEffects` (à cause du
 * `.superRefine`), qui n'expose pas `.omit(...)`. On expose donc une variante
 * pré-omise pour les consumers `insertJpAnalysisBatch` qui doivent valider
 * leurs rows avant écriture.
 */
export const PipelineJpAnalysisLogInsertSchema = _PipelineJpAnalysisLogRowBaseSchema
  .omit({ id: true, created_at: true })
  .superRefine(applyJpAnalysisLogRowRefinement);
export type PipelineJpAnalysisLogInsert = Omit<PipelineJpAnalysisLogRow, 'id' | 'created_at'>;

// ─────────────────────────────────────────────────────────────────────
// Ligne `pipeline_strategist_log` (1 par décision per-argument)
// ─────────────────────────────────────────────────────────────────────

export const PipelineStrategistLogRowSchema = z.object({
  id: z.string().uuid(),
  job_id: z.string().uuid(),
  argument_id: z.string(),

  decision: z.enum(['retenu', 'ecarte']),
  motif_decision: z.string(),
  pouvoir_active: z.enum(['refonder', 'expliciter', 'lacher', 'aucun']).nullable(),

  // Si refonder
  majeure_originale: z.string().nullable(),
  majeure_refondee: z.string().nullable(),

  // Bundle structuré pour ⑦ (StrategistDecision.argument_redige sérialisé)
  bundle: StrategistDecisionSchema.nullable(),

  // Trace LLM
  model: z.string(),
  tokens_in: z.number().int().min(0).nullable(),
  tokens_out: z.number().int().min(0).nullable(),
  latency_ms: z.number().int().min(0).nullable(),
  created_at: z.string(),
}).strict();
export type PipelineStrategistLogRow = z.infer<typeof PipelineStrategistLogRowSchema>;
